/**
 * Module dependencies.
 */

import React, { ReactNode, useCallback, useMemo } from 'react';
import VirtualList, { ItemInfo } from 'react-tiny-virtual-list';
import { useElementDimensions } from 'src/hooks/use-element-dimensions';
import styled from 'styled-components';

import { EmptyResultsOption } from './empty-results-option';

/**
 * Options.
 */

type Option = Record<'label' | 'value', string>;

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  getValue: () => [Option];
  maxHeight: number;
  options: Option[];
};

/**
 * `sizes` constant.
 */

const sizes = {
  large: 88,
  medium: 64,
  small: 48
} as const;

/**
 * `StyledEmptyResultsOption` styled component.
 */

const StyledEmptyResultsOption = styled(EmptyResultsOption)`
  align-items: center;
  display: flex;
  min-height: 70px;
`;

/**
 * `getItemHeight` util.
 */

function getItemHeight(ratio: number) {
  if (ratio > 9) {
    return sizes.small;
  }

  return ratio < 4.5 ? sizes.large : sizes.medium;
}

/**
 * `calculateOffset` util.
 */

function calculateOffset(activeIndex: number | undefined, labelsLength: number[], width: number) {
  if (!activeIndex) {
    return 0;
  }

  return labelsLength.slice(0, activeIndex).reduce((acc, length) => acc + getItemHeight(width / length), 0);
}

/**
 * Export `VirtualizedMenuList` component.
 */

export function VirtualizedMenuList(props: Props) {
  const { children, getValue, maxHeight, options } = props;
  const [option] = getValue();
  const childrenOptions = React.Children.toArray(children);
  const labelsLength = useMemo(() => options.map(item => item.label?.length), [options]);

  const totalHeight = childrenOptions.length * sizes.medium;
  const wrapperHeight = maxHeight < totalHeight ? maxHeight : totalHeight;
  const activeIndex = useMemo(() => {
    return option ? options.indexOf(option) : undefined;
  }, [option, options]);

  const [wrapperRef, { width }] = useElementDimensions<HTMLDivElement>();
  const renderItem = useCallback(
    ({ index, style }: ItemInfo) => {
      if (!Array.isArray(children)) {
        return <StyledEmptyResultsOption />;
      }

      return (
        <span key={index} style={style}>
          {children[index]}
        </span>
      );
    },
    [children]
  );

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      {width > 0 && (
        <VirtualList
          height={wrapperHeight + 6}
          itemCount={childrenOptions.length}
          itemSize={index => getItemHeight(width / labelsLength[index])}
          renderItem={renderItem}
          scrollOffset={calculateOffset(activeIndex, labelsLength, width)}
          width={'100%'}
        />
      )}
    </div>
  );
}
